import ReactGA from "react-ga4";
import { clarity } from "react-microsoft-clarity";

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const CLARITY_PROJECT_ID = import.meta.env.VITE_CLARITY_PROJECT_ID;
const ENV = import.meta.env.VITE_ENV || "DEV";

/**
 * Initialize Google Analytics with cross-domain tracking
 */
export const initGA = () => {
  if (GA_MEASUREMENT_ID) {
    ReactGA.initialize(GA_MEASUREMENT_ID, {
      gtagOptions: {
        send_page_view: true,
        // Cross-domain tracking for all your domains
        linker: {
          domains: ["focusmap.app", "preview.focusmap.app", "focusmap.dk"],
        },
        cookie_domain: "auto",
        cookie_flags: "SameSite=None;Secure",
        debug_mode: ENV !== "PROD",
      },
    });
    console.log(`✅ Google Analytics initialized [${ENV}]:`, GA_MEASUREMENT_ID);
  } else {
    console.warn("⚠️ GA Measurement ID not found");
  }
};

/**
 * Initialize Microsoft Clarity for session recordings and heatmaps
 */
export const initClarity = () => {
  if (CLARITY_PROJECT_ID) {
    try {
      clarity.init(CLARITY_PROJECT_ID);
      console.log(
        `✅ Microsoft Clarity initialized [${ENV}]:`,
        CLARITY_PROJECT_ID
      );
    } catch (error) {
      console.warn("⚠️ Clarity initialization failed:", error);
    }
  } else {
    console.warn("⚠️ Clarity Project ID not found");
  }
};

/**
 * Initialize all analytics services
 */
export const initAnalytics = () => {
  initGA();
  initClarity();
  console.log("📊 Analytics services initialized");
};

/**
 * Track page views in both GA and Clarity
 */
export const logPageView = (path: string) => {
  if (GA_MEASUREMENT_ID) {
    ReactGA.send({
      hitType: "pageview",
      page: path,
      title: document.title,
    });
    console.log("📊 GA Page View:", path);
  }

  // Clarity automatically tracks page views
};

/**
 * Track custom events in both GA and Clarity
 */
export const logEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number
) => {
  // Google Analytics
  if (GA_MEASUREMENT_ID) {
    ReactGA.event({
      category: category,
      action: action,
      label: label,
      value: value,
    });
    console.log("📊 GA Event:", { category, action, label, value });
  }

  // Clarity doesn't have a direct event method in react-microsoft-clarity
  // Events are automatically tracked based on user interactions
};

/**
 * Identify user in both GA and Clarity
 */
export const identifyUser = (
  userId: string,
  userProperties?: Record<string, any>
) => {
  // Google Analytics
  if (GA_MEASUREMENT_ID) {
    ReactGA.set({ userId, ...userProperties });
    console.log("📊 GA User identified:", userId);
  }

  // Clarity - identify user with optional custom properties
  if (CLARITY_PROJECT_ID) {
    try {
      clarity.identify(userId, userProperties || {});
    } catch (error) {
      // Silently handle errors
    }
  }
};

/**
 * Track timing events (e.g., page load time, API response time)
 */
export const logTiming = (
  category: string,
  variable: string,
  value: number,
  label?: string
) => {
  if (GA_MEASUREMENT_ID) {
    ReactGA.gtag("event", "timing_complete", {
      name: variable,
      value: value,
      event_category: category,
      event_label: label,
    });
    console.log("📊 GA Timing:", { category, variable, value, label });
  }
};

/**
 * Track exceptions/errors
 */
export const logException = (description: string, fatal: boolean = false) => {
  if (GA_MEASUREMENT_ID) {
    ReactGA.gtag("event", "exception", {
      description: description,
      fatal: fatal,
    });
    console.log("📊 GA Exception:", { description, fatal });
  }
};
